import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, MapPin, Clock, GraduationCap, Building2, Zap, ArrowRight, ShieldAlert, EyeOff } from 'lucide-react';
import { werkplekApi, type MarktOpdracht } from '../../services/professionalWerkplekApi';
import { PROFESSIONAL_TYPE_LABELS, type ProfessionalType } from '../../types/professionalRates';
import { useBlokkeerOpdrachtgever } from './blokkeren';

const AANTAL = 6;

/** Ervaringsband uit de opdracht: expliciete eis, anders afgeleid van het minimum aantal jaren. */
function ervaringsband(o: MarktOpdracht): string | null {
  if (o.experience_level_required) return o.experience_level_required;
  const j = o.years_experience_min;
  if (j === null || j === undefined) return null;
  return j <= 2 ? '0-2' : j <= 5 ? '3-5' : j <= 10 ? '6-10' : '11+';
}

/** Onderdeel 3: de nieuwste opdrachten uit de marktplaats; het volledige overzicht staat onder Dashboard › Opdrachten. */
export const MarktplaatsBlok: React.FC = () => {
  const { t } = useTranslation('professionalDashboard');
  const { data, isLoading } = useQuery({
    queryKey: ['professional-werkplek-opdrachten', 'landelijk', '', 1],
    queryFn: () => werkplekApi.opdrachten({ straal: 'landelijk', page: 1 }),
  });
  const opdrachten = (data?.opdrachten ?? []).slice(0, AANTAL);
  const verberg = useBlokkeerOpdrachtgever();

  return (
    <section aria-labelledby="marktplaats-titel" className="rounded-2xl border border-gray-200 bg-white shadow-card">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 px-5 pt-5 pb-3 border-b border-gray-100">
        <div>
          <h2 id="marktplaats-titel" className="flex items-center gap-2 text-base font-semibold text-navy-900">
            <ShoppingCart className="w-5 h-5" />
            {t('marktplaats.titel')}
            {(data?.pagination.total ?? 0) > 0 && (
              <span className="rounded-full bg-navy-50 px-2 py-0.5 text-xs font-semibold text-navy-700">{data!.pagination.total}</span>
            )}
          </h2>
          <p className="text-xs text-navy-500 mt-0.5">{t('marktplaats.ondertitel')}</p>
        </div>
        <Link to="/professional/dashboard/opdrachten" className="inline-flex items-center gap-1 text-sm font-semibold text-navy-700 hover:text-navy-900">
          {t('marktplaats.alle')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </header>

      <div className="p-4">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-36 rounded-xl bg-gray-50 animate-pulse" />
            ))}
          </div>
        ) : data?.blokkade ? (
          <p className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />
            {t(`marktplaats.blokkade.${data.blokkade}`)}
          </p>
        ) : opdrachten.length === 0 ? (
          <p className="rounded-xl bg-canvas p-6 text-center text-sm text-navy-500">{t('marktplaats.leeg')}</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {opdrachten.map((o) => {
              const band = ervaringsband(o);
              const regio = o.provincie || o.company_location || (o.active_regions?.length ? o.active_regions.join(', ') : t('marktplaats.landelijk'));
              return (
                <li key={o.id} className="flex flex-col rounded-xl border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition">
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {o.organisatie_logo ? <img src={o.organisatie_logo} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-4 h-4 text-navy-700" />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-navy-500 truncate">{o.organisatie || PROFESSIONAL_TYPE_LABELS[o.professional_type as ProfessionalType] || o.professional_type}</p>
                      <h3 className="text-sm font-semibold text-navy-900 line-clamp-2">{o.titel}</h3>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-navy-500">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {regio}
                    </span>
                    {o.hours_per_month_staffel && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {t('marktplaats.urenPerMaand', { uren: o.hours_per_month_staffel })}
                      </span>
                    )}
                    {band && (
                      <span className="inline-flex items-center gap-1">
                        <GraduationCap className="w-3.5 h-3.5" />
                        {t('marktplaats.ervaring', { band: band.replace('-', '–') })}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                    {o.application_status ? (
                      <span className="inline-block rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                        {t('marktplaats.gereageerd')}
                      </span>
                    ) : (
                      <Link
                        to={`/opdrachten/${o.id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-gold-500 px-3 py-1.5 text-xs font-semibold text-navy-900 hover:bg-gold-400"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        {t('marktplaats.reageer')}
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => verberg.blokkeer(o.employer_id, o.organisatie)}
                      disabled={verberg.bezig}
                      title={t('blacklist.verbergen')}
                      aria-label={t('blacklist.verbergen')}
                      className="p-1.5 text-navy-300 hover:text-navy-700"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};
